import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { makeRequest } from "@/makeRequest";
import { MY_ENDPOINTS } from "@raashidh/shared";
import { useLang } from "../context/TranslationProvider";
import { ArrowLeft, Download, Calendar, Building2, Clock, ExternalLink } from "lucide-react";

type AnnouncementDetail = {
    id: string;
    type: string;
    office: string;
    title: string;
    announcementNumber: string;
    publishedDate: string;
    publishedTime: string;
    deadline: string;
    pdfUrl: string;
    bodyHtml: string;
    content: {
        position: string;
        positionNumber: string;
        quantity: string;
        nature: string;
        rank: string;
        location: string;
        salary: string;
        jobAllowances: string;
        otherAllowances: string;
        duties: string[];
        qualifications: string;
        requiredDocuments: string[];
        applicationDeadline: string;
        interviewPeriod: string;
    };
};

export default function GazetteAnnouncementDetail({ uid, deadline, url }: { uid?: any, deadline?: string, url?: string }) {
    const { t, direction } = useLang();
    const isRTL = direction === 'rtl';
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const announcementId = uid || id;

    const [detail, setDetail] = useState<AnnouncementDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!announcementId) {
            setError("Announcement ID is missing");
            setLoading(false);
            return;
        }

        fetchAnnouncementDetail(announcementId);
    }, [announcementId]);

    const fetchAnnouncementDetail = async (announcementId: string) => {
        try {
            setLoading(true);
            const response = await makeRequest<{
                success: boolean;
                data: AnnouncementDetail;
            }>(MY_ENDPOINTS.SCRAPER_ENDPOINTS.GET_GAZETTE_ANNOUNCEMENT_DETAIL?.path, {
                method: "GET",
                query: { id: announcementId },
                auth: true,
            });

            if (response.success) {
                setDetail(response.data);
            } else {
                setError("Failed to load announcement details");
            }
        } catch (err) {
            console.error(err);
            setError("Something went wrong while fetching details");
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadPDF = () => {
        if (detail?.pdfUrl) {
            window.open(detail?.pdfUrl, "_blank");
        }
    };

    const handleExternalLink = () => {
        if (url) {
            // If it's a relative path, prepend base URL if needed
            let fullUrl = url;
            if (url.startsWith('/')) {
                fullUrl = window.location.origin + url;
            }
            window.open(fullUrl, "_blank", "noopener,noreferrer");
        } else if (detail?.pdfUrl) {
            // Fallback to PDF if no URL provided
            window.open(detail.pdfUrl, "_blank");
        } else {
            console.warn("No URL or PDF available");
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen bg-white dark:bg-gray-900">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400"></div>
            </div>
        );
    }

    if (error || !detail) {
        return (
            <div className="text-center py-20 bg-white dark:bg-gray-900 min-h-screen">
                <div className="text-5xl mb-4">⚠️</div>
                <h2 className="text-xl font-semibold text-red-600 dark:text-red-400 mb-2">Failed to Load</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">{error || "No data available"}</p>
                <button
                    onClick={() => navigate(-1)}
                    className="px-6 py-3 bg-gray-700 dark:bg-gray-600 text-white rounded-lg hover:bg-gray-800 dark:hover:bg-gray-700 transition"
                >
                    Go Back
                </button>
            </div>
        );
    }

    const displayDeadline = deadline || detail?.deadline;

    return (
        <div className={`dhi bg-white dark:bg-gray-900 min-h-screen ${isRTL ? 'rtl' : 'ltr'}`} dir={direction}>
            <div className="container mx-auto px-2 py-8 max-w-4xl">
                {/* Back Button */}
                <button
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 mb-6 transition-colors"
                >
                    <ArrowLeft className="w-5 h-5" />
                    <span>{t('back')}</span>
                </button>

                {/* Header */}
                <div className="mb-8">
                    <div className="flex flex-wrap gap-3 items-center mb-4">
                        <span className="px-4 py-1.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-sm font-medium">
                            {detail?.type}
                        </span>
                        <span className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                            <Building2 className="w-4 h-4" />
                            {detail?.office}
                        </span>
                    </div>

                    <h1 className="text-3xl leading-tight font-bold text-gray-900 dark:text-white mb-4">
                        {detail?.title}
                    </h1>

                    <div className="flex flex-wrap gap-6 text-sm text-gray-600 dark:text-gray-400">
                        <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            <span>{detail?.publishedDate}</span>
                        </div>
                        {displayDeadline && (
                            <div className="flex items-center gap-2 text-red-600 dark:text-red-400 font-medium">
                                <Clock className="w-4 h-4" />
                                <span>ސުންގަޑި: {displayDeadline}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-3 mb-8">
                    {/* External Link Button - Improved */}
                    {(url || detail?.pdfUrl) && (
                        <button
                            onClick={handleExternalLink}
                            className="flex items-center gap-2 px-6 py-3 bg-blue-600 dark:bg-blue-700 hover:bg-blue-700 dark:hover:bg-blue-800 text-white rounded-lg transition-colors shadow-md group"
                        >
                            <span>{t("announcement.readMore") || "Read More"}</span>
                            <ExternalLink className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                        </button>
                    )}

                </div>

                {/* Announcement Number */}
                {detail?.announcementNumber && (
                    <div className="bg-gray-100 dark:bg-gray-800/50 p-4 rounded-lg mb-8 border border-gray-200 dark:border-gray-700">
                        <p className="text-sm text-gray-500 dark:text-gray-400">އިއުލާން ނަންބަރު</p>
                        <p className="font-mono text-lg font-semibold text-gray-900 dark:text-white">
                            {detail?.announcementNumber}
                        </p>
                    </div>
                )}

                {/* Main Content */}
                <div className="w-full dhivehi-detail">


                    {/* Application Deadline & Interview */}
                    {(detail?.content?.applicationDeadline || detail?.content?.interviewPeriod) && (
                        <div className="bg-blue-50 dark:bg-blue-950/30 p-6 rounded-xl border border-blue-100 dark:border-blue-900">
                            <h3 className="font-semibold mb-4 text-lg text-gray-900 dark:text-white">މަޤާމަށް އެދެންވީ ގޮތާއި ސުންގަޑި</h3>
                            <div className="space-y-3 text-[15px] text-gray-700 dark:text-gray-300">
                                {detail?.content?.applicationDeadline && (
                                    <p><strong className="text-gray-900 dark:text-white">ސުންގަޑި:</strong> {detail?.content?.applicationDeadline}</p>
                                )}
                                {detail?.content?.interviewPeriod && (
                                    <p><strong className="text-gray-900 dark:text-white">އިންޓަރވިއު އަދި އިމްތިހާން:</strong> {detail?.content?.interviewPeriod}</p>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                <div
                    className="w-full max-w-full overflow-x-hidden dhivehi-html rtl-content"
                    style={{
                        direction: "rtl",
                        textAlign: "right",
                    }}
                >
                    <div
                        className="w-full max-w-full break-words overflow-x-hidden"
                        dangerouslySetInnerHTML={{
                            __html: detail.bodyHtml,
                        }}
                    />
                </div>

                {/* Footer Note */}
                <div className="text-center text-xs text-gray-500 dark:text-gray-500 mt-12 pt-8 border-t border-gray-200 dark:border-gray-800">
                    ދިވެހިރާއްޖޭގެ ގެޒެޓުން ނަކަލުކުރެވިފައި
                </div>
            </div>
        </div>
    );
}

export const getFirstSentence = (
    text: string,
    separator: string = "ވެ."
): string => {
    if (!text) return "";

    const index = text?.indexOf(separator);

    if (index === -1) {
        return text;
    }

    return text?.substring(0, index + separator.length).trim();
};